import debounce from 'lodash.debounce';
import dynamic from 'next/dynamic';
import { rem } from 'polished';
import React, { ReactElement, useState, MouseEventHandler, useEffect, useContext } from 'react';
import styled from 'styled-components';

import { translate } from '@vimeo/i18n-runtime';
import { ViewerContext } from '@vimeo/viewer';

import { MODAL_TYPE_LOGIN } from './constants';
import { JoinForm, LoginForm, ConnectToFbForm } from './forms';
import SSOForm from './forms/SSOForm';
import { validateSSOEmail, loginViaSSO, ssoDataInitialState } from './forms/SSOLogin';
import { BpData, trackViewSignupLoginScreen } from './tracking';

const RegModal = dynamic(async () => {
  const Modal = await import('./UnifiedLoginJoinModal');
  return Modal;
});

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 ${rem(32)};

  @media screen and (max-width: ${rem(479)}) {
    margin: 0;
  }
`;

interface LoginProps {
  xsrft: string;
  type: 'login' | 'join';
  shouldOptIntoMarketing?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSuccess?: (response: any) => void;
  switchType?: MouseEventHandler;
  faContainer?: string;
  redirectUrl?: string;
  name?: string;
  email?: string;
  createTemplates?: boolean;
  redirectUrlAfterSocialLogin?: string;
  target?: string;
  showFBCreativeConnectJoinModal?: boolean;
}

interface ModalProps extends LoginProps {
  className?: string;
  /** When children is optional, use isShowing and onDismiss to set modal to be controlled */
  children?: (toggleModal: () => void) => ReactElement;
  isShowing?: boolean;
  disableDismiss?: boolean;
  onDismiss?: () => void;
  updateInitialType?: (type: 'login' | 'join') => void;
  bpData?: BpData;
}

export function LoginContainer({
  onSuccess,
  shouldOptIntoMarketing,
  type,
  xsrft,
  switchType,
  faContainer,
  redirectUrl,
  name,
  email,
  redirectUrlAfterSocialLogin = '',
  target,
  showFBCreativeConnectJoinModal,
}: LoginProps) {
  const [ssoData, updateSSOData] = useState(ssoDataInitialState);
  const [enteredEmail, setEnteredEmail] = useState(email);
  const [showSSOFlow, updateShowSSOFlow] = useState(ssoData.SSO);
  const viewer = useContext(ViewerContext);

  useEffect(() => {
    if (viewer) {
      trackViewSignupLoginScreen(faContainer, viewer, showFBCreativeConnectJoinModal, type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSSOLogin = () =>
    loginViaSSO(ssoData.connection, ssoData.prevPage || redirectUrl, ssoData?.customDomain);
  const onSSOFlowChoice = (activateSSOFlow: boolean) => {
    setEnteredEmail('');
    updateShowSSOFlow(activateSSOFlow);
  };

  const onEmailChange = (email: string) => {
    setEnteredEmail(email);
    return validateSSOEmail(email, updateSSOData);
  };

  return (
    <Container>
      {ssoData.SSO || showSSOFlow ? (
        <SSOForm
          email={enteredEmail}
          isSSO={ssoData.SSO}
          onEmailChange={debounce(onEmailChange, 500)}
          handleSSOLogin={handleSSOLogin}
          checkInProgress={ssoData.checkInProgress}
          onSSOFlowChoice={onSSOFlowChoice}
          firstTimeSSOLogin={ssoData.firstTimeSSOLogin}
          switchType={switchType}
        />
      ) : type === MODAL_TYPE_LOGIN ? (
        <LoginForm
          xsrft={xsrft}
          switchType={switchType}
          onSuccess={onSuccess}
          fa={
            faContainer
              ? {
                  container: faContainer,
                  thirdPartyIntegration: showFBCreativeConnectJoinModal ? 'Facebook' : 'none',
                }
              : undefined
          }
          email={enteredEmail}
          onEmailChange={debounce(onEmailChange, 500)}
          redirectUrl={redirectUrl}
          redirectUrlAfterSocialLogin={redirectUrlAfterSocialLogin}
          target={target}
          onSSOFlowChoice={onSSOFlowChoice}
          showEnterpriseOptInConsent={ssoData.showEnterpriseOptInConsent}
          isF2PVariant={false}
        />
      ) : showFBCreativeConnectJoinModal && window.location.pathname.indexOf('/create') !== -1 ? (
        <ConnectToFbForm
          onSuccess={onSuccess}
          switchType={switchType}
          xsrft={xsrft}
          shouldOptIntoMarketing={shouldOptIntoMarketing}
          redirectUrl={redirectUrl}
          target={target}
        />
      ) : (
        <JoinForm
          onSuccess={onSuccess}
          shouldOptIntoMarketing={shouldOptIntoMarketing}
          switchType={switchType}
          xsrft={xsrft}
          fa={
            faContainer
              ? {
                  container: faContainer,
                  thirdPartyIntegration: showFBCreativeConnectJoinModal ? 'Facebook' : 'none',
                }
              : undefined
          }
          name={name}
          email={enteredEmail}
          onEmailChange={debounce(onEmailChange, 500)}
          redirectUrl={redirectUrl}
          redirectUrlAfterSocialLogin={redirectUrlAfterSocialLogin}
          onSSOFlowChoice={onSSOFlowChoice}
          placeholder={{
            name: translate('First and last name'),
            email: translate('Email address'),
            password: translate('Password'),
          }}
        />
      )}
    </Container>
  );
}

export function LoginJoinModal({
  children,
  onSuccess,
  type: initialType,
  isShowing,
  disableDismiss,
  onDismiss,
  redirectUrlAfterSocialLogin,
  showFBCreativeConnectJoinModal,
  redirectUrl,
  target,
  updateInitialType,
  bpData,
}: ModalProps) {
  const [active, setActive] = useState(!!isShowing);
  const [type, setType] = useState(initialType);

  useEffect(() => {
    setType(initialType);
  }, [initialType]);

  useEffect(() => {
    setActive(!!isShowing);
  }, [isShowing]);

  const toggleModal = () => {
    setActive((isActive) => !isActive);
  };

  const handleDismissModal = () => {
    toggleModal();
    if (onDismiss) {
      onDismiss();
    }
    setType(initialType);
  };

  const updateType = (type: 'login' | 'join') => {
    if (updateInitialType) {
      updateInitialType(type);
    }
    setType(type);
  };

  return (
    <>
      {children ? children(toggleModal) : null}
      <RegModal
        type={type}
        active={active}
        disableDismiss={disableDismiss}
        redirectUrlAfterSocialLogin={redirectUrlAfterSocialLogin}
        redirectUrlFbForm={redirectUrl}
        showFBCreativeConnectJoinModal={showFBCreativeConnectJoinModal}
        target={target}
        redirectUrl={redirectUrl}
        onDismiss={handleDismissModal}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSuccess={(response: any) => {
          toggleModal();
          if (onSuccess) {
            onSuccess(response);
          }
        }}
        updateType={updateType}
        bpData={bpData}
      />
    </>
  );
}

/**
 * Prevent modal header from inheriting page styles and becoming misaligned
 * due to margin-left on span in h2 in iris legacy modal
 */
export const DefaultStyledLoginJoinModal = styled(LoginJoinModal)`
  #joinModal,
  #loginModal {
    span {
      margin-left: 0;
    }
  }
`;
